import ZingTouch from 'zingtouch';
import gsap from 'gsap';
import Nearby from '../shared/classes/Nearby';
import { getCardinalDirection } from '../shared/helpers/ZingTouchHelper';



/* 
 * 
 */
export default class HorizontalList {
	#wrapper; 
	#touchableEl;
	#effectEl;
	#elements;
	#itemWidth;
	#realWidth;
	#track;
	#position = 1;
	
	/*
	 * 
	 */
	constructor(wrapper, touchableEl, effectEl, itemsSelector) {
		this.#wrapper = wrapper;
		this.#touchableEl = touchableEl;
		this.#effectEl =  effectEl;
		this.#elements = Array.from(this.#wrapper.querySelectorAll(itemsSelector));
		this.getRealWidth();
		this.bindEvents();
	}
	
	
	
	/*
	 * 
	 */
	withNearbyFeedback() {
		const previous = this.#wrapper.querySelector('.track--prev-screen');
		const next = this.#wrapper.querySelector('.track--next-screen');
		if(! previous && ! next ) {
			return;
		}
		
		let onprev;
		let onnext;
		
		// whatever we do, start at [distanceThreshold.max]px from the
		// element and end at [distanceThreshold.min]px from the element.
		const distanceThreshold = {
			min: 0, 
			max: 200,
		};
		
		
		const widthInterval = {
			from: 48, 
			to: 180,
		};
		
		const opacityInterval = { 
			from: 0, 
			to: 1, 
		};
		
		// Equation of a line.
		// y = mx + b 
		const lineEq = (y2, y1, x2, x1, currentVal) => {
			var m = (y2 - y1) / (x2 - x1), b = y1 - m * x1;
			return m * currentVal + b;
		};
		
		
		const _change = (element, distance) => {
			const tx = lineEq(
				widthInterval.from, 
				widthInterval.to, 
				distanceThreshold.max, 
				distanceThreshold.min, 
				distance
			);
			
			const o = lineEq(
				opacityInterval.from, 
				opacityInterval.to, 
				distanceThreshold.max, 
				distanceThreshold.min, 
				distance
			);
			
			gsap.to(element, {
				width: Math.min(
					Math.max(widthInterval.from, tx), 
					widthInterval.to
				),
				duration: 1,
				//overwrite: 'auto',
				ease: 'power4.easeOut',
			});
			
			
			gsap.to(element, {
				opacity: gsap.utils.clamp(
					opacityInterval.from, 
					opacityInterval.to, 
					Math.max(o, opacityInterval.from)
				),
				duration: .5,
				//overwrite: 'auto',
				ease: 'expo.easeOut',
			});
		}
		
		
		if( previous ) {
			new Nearby(previous, {
				onProgress: (distance) => {
					onprev = distance <= distanceThreshold.max && distance >= distanceThreshold.min;
					if (! onnext ) {
						_change(previous, distance);
					}
				}
			});
			
			previous.addEventListener('click', () => this.moveTo('E'));
		}
		
		if( next ) {
			new Nearby(next, {
				onProgress: (distance) => {
					onnext = distance <= distanceThreshold.max && distance >= distanceThreshold.min;
					if (! onprev ) {
						_change(next, distance);
					}
				}
			});
			
			next.addEventListener('click', () => this.moveTo('W'));
		}
	}
	
	
	/*
	 * 
	 */
	attachTrack(track) {
		if(! track ) {
			return;
		}
		
		const _this = this;
		this.#track = track;
		this.#track.onChange(percentage => {
			const { min } = _this.getBounds();
			const _x = (min / 100) * percentage;
			_this.setPosition(_x);
		});
		
		const minTrackWidthPercentage = 100 / (this.#realWidth / this.#effectEl.offsetWidth);
		_this.#track.setPercentage( minTrackWidthPercentage );
	}
	
	
	/*
	 * 
	 */
	getRealWidth() {
		let _width = 0;
		if( this.#elements.length === 0 ) {
			return 0;
		}
	
		this.#itemWidth = this.#elements[0].offsetWidth;
		this.#elements.forEach(i => _width += i.offsetWidth);
		this.#realWidth = _width;
		return _width;
	}
	
	
	/*
	 * 
	 */
	getBounds() {
		const min = this.#effectEl.offsetWidth - this.#realWidth;
		const max = 0;
		return { min, max };
	}
	
	
	/*
	 * 
	 */
	bindEvents() {
		const _this = this;
		const region = ZingTouch.Region(this.#wrapper, false, false);
		
		
		window.addEventListener('resize', () => _this.getRealWidth());
		
		const panEvent = new ZingTouch.Pan({
			numInputs: 1,
			threshold: 50,
		});
		
		region.bind(this.#touchableEl, panEvent, event => {
			const { currentDirection } = event.detail.data[0];
			const direction = getCardinalDirection(currentDirection);
			
			if( direction === 'W' || direction === 'E' ) {
				_this.moveTo(direction);
				event.detail.events.forEach(e => e.originalEvent.preventDefault());
				return;
			}
		}, false);
	}
	
	
	/*
	 * 
	 */
	moveTo(direction) {
		if( direction === 'W' || direction === 'E' ) {
			const viewWidth = this.#effectEl.offsetWidth;
			const visibleItems = Math.floor(viewWidth / (this.#itemWidth - 1)); // let 1 pixel of approx
			const distance = visibleItems * this.#itemWidth;
			const factor = direction === 'W' ? -1 : 1;
			this.setPosition(this.#position + (distance * factor));
			return;
		}
	}
	
	
	/*
	 * 
	 */
	setPosition(pos) {
		const _this = this;
		const bounds = _this.getBounds();
		const x = gsap.utils.clamp(bounds.min, bounds.max, pos);
		const trackMinWidth = 100 / (this.#realWidth / this.#effectEl.offsetWidth);
		
		gsap.to(_this.#effectEl, { 
			x: x,
			duration: .5, 
			onStart: () => _this.#track && _this.#track.setPercentage(trackMinWidth +( (x / bounds.min) * (100 - trackMinWidth) ) ),
		});
		
		this.#position = pos;
	}
}